import * as React from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Table from '@mui/joy/Table';
import Sheet from '@mui/joy/Sheet';
import Link from '@mui/joy/Link';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import Tabs from '@mui/joy/Tabs';
import TabList from '@mui/joy/TabList';
import Tab from '@mui/joy/Tab';
import CircularProgress from '@mui/material/CircularProgress';
import { getAppointments } from '../../../service/ConsultationService';
import { IConsultation } from '../../../service/Consultation';
import { useAppContext } from '../../../contexts/AppContext';

interface AppointmentListProps {
  onEdit: (appointmentId: number) => void;
  showSnackBar: (message: string) => void;
}

const AppointmentList: React.FC<AppointmentListProps> = ({ onEdit, showSnackBar }) => {
  const [appointments, setAppointments] = React.useState<IConsultation[]>([]);
  const { clinic, doctor, loading } = useAppContext();
  const [activeTab, setActiveTab] = React.useState<number>(0); // 0 for Pending, 1 for Completed
  const [dataLoading, setDataLoading] = React.useState<boolean>(true); // Added state to track loading status

  const fetchAppointments = async () => {
    if (!loading && doctor) {
      try {
        const data = await getAppointments();
        setAppointments(data);
      } catch (error) {
        console.error(error);
        showSnackBar('Failed to load appointments.');
      } finally {
        setDataLoading(false); // Set loading to false after data is fetched
      }
    }
  };

  React.useEffect(() => {
    fetchAppointments();
  }, [loading, doctor]);

  const handleTabChange = (event: React.SyntheticEvent | null, newValue: string | number | null) => {
    if (typeof newValue === 'number') {
      setActiveTab(newValue);
    }
  };

  const filteredAppointments = appointments.filter((appointment) => {
    if (activeTab === 0) {
      return appointment.status === 'APPOINTMENT_CREATED';
    } else if (activeTab === 1) {
      return appointment.status === 'CONSULTATION_COMPLETED';
    }
    return false;
  });

  const tableHeight = Math.min(filteredAppointments.length * 52 + 56, window.innerHeight - 200); // 52 is approximate row height, 56 is the header height

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs
        aria-label="Appointment tabs"
        value={activeTab}
        onChange={handleTabChange}
        sx={{ bgcolor: 'transparent' }}
      >
        <TabList
          disableUnderline
          sx={{
            p: 0.5,
            gap: 0.5,
            borderRadius: 'xl',
            bgcolor: 'background.surface', // Adjusted background color for compatibility with both light and dark modes
            [`& .MuiTab-root[aria-selected="true"]`]: {
              boxShadow: 'sm',
              bgcolor: 'primary.500', // Slightly different background for the selected tab
            },
            width: 'fit-content', // Width is now based on the content
            minWidth: 'fit-content', // Ensures the width does not shrink too much
            margin: 'auto', // Centers the TabList horizontally
          }}
        >
          <Tab
            disableIndicator
            sx={{
              [`&[aria-selected="true"]`]: {
                color: 'white', // Ensures the text color remains white when the tab is selected
              },
            }}
          >
            Pending
          </Tab>
          <Tab
            disableIndicator
            sx={{
              [`&[aria-selected="true"]`]: {
                color: 'white', // Ensures the text color remains white when the tab is selected
              },
            }}
          >
            Completed
          </Tab>
        </TabList>
      </Tabs>

      {dataLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '80vh', // Adjust height as needed
          }}
        >
          <CircularProgress /> {/* Show a loading spinner */}
        </Box>
      ) : filteredAppointments.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '80vh', // Adjust height as needed
            textAlign: 'center',
          }}
        >
          <Typography level="body-md" component="h2" mb={2}>
            {activeTab === 0 ? 'No Pending Appointments Today' : 'No Completed Appointments Found'}
          </Typography>
        </Box>
      ) : (
        <Sheet
          className="AppointmentTableContainer"
          variant="outlined"
          sx={{
            width: '100%',
            maxHeight: '80vh', // Maximum height of the table
            height: tableHeight,
            borderRadius: 'sm',
            flexShrink: 1,
            overflow: 'auto',
            minHeight: 0,
            mt: 2, // Added margin top to give space between tabs and table
          }}
        >
          <Table
            aria-labelledby="tableTitle"
            stickyHeader
            hoverRow
            sx={{
              '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
              '--Table-headerUnderlineThickness': '1px',
              '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
              '--TableCell-paddingY': '4px',
              '--TableCell-paddingX': '8px',
              overflow: 'auto', // Enable scrolling within the table
            }}
          >
            <thead>
              <tr>
                <th style={{ width: 100, padding: '12px 6px' }}>Token Number</th>
                <th style={{ width: 100, padding: '12px 6px' }}>Patient ID</th>
                <th style={{ width: 150, padding: '12px 6px' }}>Patient Name</th>
                <th style={{ width: 150, padding: '12px 6px' }}>Mobile Number</th>
                <th style={{ width: 200, padding: '12px 6px' }}>Symptoms</th>
                <th style={{ width: 100, padding: '12px 6px' }}>Fees</th>
                <th style={{ width: 150, padding: '12px 6px' }}>Payment Mode</th>
                <th style={{ width: 120, padding: '12px 6px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAppointments.map((appointment) => (
                <tr key={appointment.id}>
                  <td>
                    <Typography level="body-xs">{appointment.tokenNumber}</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{appointment.patient.displayId}</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{appointment.patient.name}</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{appointment.patient.mobileNumber}</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {appointment.complaints?.map((complaint) => complaint.type).join(', ')}
                    </Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{appointment.fees}</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{appointment.paymentMode}</Typography>
                  </td>
                  <td>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Link
                        level="body-xs"
                        component="button"
                        startDecorator={<EditRoundedIcon />}
                        onClick={() => onEdit(appointment.id)}
                      >
                        Edit
                      </Link>
                    </Box>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
      )}
    </Box>
  );
};

export default AppointmentList;
